'use client'

interface BlogContentRendererProps {
  content: string
}

export function BlogContentRenderer({ content }: BlogContentRendererProps) {
  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let currentList: string[] = []
  let listKey = 0

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${listKey++}`} className="list-disc list-inside mb-4 space-y-2 ml-4">
          {currentList.map((item, idx) => (
            <li key={idx} className="text-gray-700">{item}</li>
          ))}
        </ul>
      )
      currentList = []
    }
  }

  lines.forEach((line, index) => {
    const trimmedLine = line.trim()

    // Headers
    if (trimmedLine.startsWith('# ')) {
      flushList()
      elements.push(
        <h1 key={index} className="text-4xl font-bold text-gray-900 mt-10 mb-4 first:mt-0">
          {trimmedLine.substring(2)}
        </h1>
      )
      return
    }
    if (trimmedLine.startsWith('## ')) {
      flushList()
      elements.push(
        <h2 key={index} className="text-3xl font-semibold text-gray-900 mt-8 mb-4">
          {trimmedLine.substring(3)}
        </h2>
      )
      return
    }
    if (trimmedLine.startsWith('### ')) {
      flushList()
      elements.push(
        <h3 key={index} className="text-2xl font-semibold text-gray-900 mt-6 mb-3">
          {trimmedLine.substring(4)}
        </h3>
      )
      return
    }

    // Lists
    if (trimmedLine.startsWith('- ')) {
      currentList.push(trimmedLine.substring(2))
      return
    }

    // Flush list if we hit a non-list item
    if (trimmedLine && currentList.length > 0) {
      flushList()
    }

    // Empty lines
    if (!trimmedLine) {
      if (currentList.length > 0) {
        flushList()
      }
      elements.push(<br key={index} />)
      return
    }

    // Regular paragraphs with bold support
    if (trimmedLine.includes('**')) {
      const parts = trimmedLine.split(/(\*\*[^*]+\*\*)/g)
      elements.push(
        <p key={index} className="mb-4 text-gray-700 leading-relaxed">
          {parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return (
                <strong key={i} className="font-semibold text-gray-900">
                  {part.substring(2, part.length - 2)}
                </strong>
              )
            }
            return <span key={i}>{part}</span>
          })}
        </p>
      )
      return
    }

    // Regular paragraphs
    if (trimmedLine && !trimmedLine.startsWith('-') && !trimmedLine.startsWith('#')) {
      elements.push(
        <p key={index} className="mb-4 text-gray-700 leading-relaxed">
          {trimmedLine}
        </p>
      )
    }
  })

  // Flush any remaining list
  flushList()

  return <div className="blog-content">{elements}</div>
}

